import type { CheckboxOption } from '@common/validation/customControls/nestedCheckboxGroupWithTextInput';

export const HolterDeviation = {
    achterwand_infarct: 'Achterwandinfarct (doorgemaakt)',
    anders: 'Anders, namelijk...',
    aspecifieke_iv_geleidingsvertraging: 'Aspecifieke IV geleidingsvertraging',
    aspecifieke_repolarisatiestoornissen: 'Aspecifieke repolarisatiestoornissen',
    atriaal_pacemakerritme: 'Atriaal pacemakerritme',
    atriale_tachycardie: 'Atriale tachycardie',
    atriumfibrilleren: 'Atriumfibrilleren',
    atriumflutter: 'Atriumflutter',
    av_geleiding: 'AV geleiding',
    av_nodaal_ritme: 'AV-nodaal ritme',
    avnrt: 'AVNRT',
    compleet_lbtb: 'Compleet LBTB',
    compleet_rbtb: 'Compleet RBTB',
    ddd_pacemakerritme: 'DDD pacemakerritme',
    ectopisch_boezemritme: 'Ectopisch boezemritme',
    eerste_graads_av_blok: 'Eerste graads AV-blok',
    elektrische_hartas: 'Elektrische hartas',
    extreme_as: 'Extreme as',
    fasciculus_anticus_blok: 'Fasciculus Anticus blok (LAHB)',
    fasciculus_posticus_blok: 'Fasciculus Posticus blok (LPHB)',
    fusie_complex: 'Fusiecomplex',
    geblokkeerde_pac_bij_av_geleiding: 'Geblokkeerde PAC bij AV geleiding',
    geen_ischemie: 'Geen ischemie',
    hoog_lateraal_infarct: 'Hoog lateraal infarct (doorgemaakt)',
    horizontale_hartas: 'Horizontale hartas',
    incompleet_lbtb: 'Incompleet LBTB',
    incompleet_rbtb: 'Incompleet RBTB',
    inferolateraal_infarct: 'Inferolateraal infarct (doorgemaakt)',
    intermediair: 'Intermediair',
    laag_voltage_in_standaard_afleidingen: 'Laag voltage in standaard afleidingen',
    linker_asdeviatie: 'Linkerasdeviatie',
    linker_boezem_overbelasting: 'Linker boezemoverbelasting',
    niet_te_bepalen_ritme: 'Niet te bepalen ritme',
    normaal_voltage: 'Normaal voltage',
    normale_geleiding: 'Normale geleiding',
    onderwand_infarct: 'Onderwandinfarct (doorgemaakt)',
    overig: 'Overig',
    pre_exitatie: 'Pre-excitatie',
    prematuur_atriaal_complex: 'Prematuur atriaal complex',
    prematuur_ventriculair_complex: 'Prematuur ventriculair complex',
    prominente_u_golf: 'Prominente U golf',
    qrs_geleiding: 'QRS geleiding',
    qrs_normale_breedte: 'QRS normale breedte',
    qrs_vorm: 'QRS vorm',
    rechter_asdeviatie: 'Rechterasdeviatie',
    rechter_boezem_overbelasting: 'Rechter boezemoverbelasting',
    ritme: 'Ritme',
    sa_block: 'SA-blok',
    secundaire_repolarisatie_stoornissen: 'Secundaire repolarisatiestoornissen',
    sinusaritmie: 'Sinusaritmie',
    sinusarrest: 'Sinusarrest',
    sinusritme: 'Sinusritme',
    sinustachycardie: 'Sinustachycardie',
    st_segment: 'ST segment',
    supraventriculair_ritmestoornis: 'Supraventriculaire ritmestoornis',
    tekenen_van_lvh: 'Tekenen van LVH',
    tekenen_van_rvh: 'Tekenen van RVH',
    torsades_des_pointes: 'Torsades de pointes',
    totaal_av_blok: 'Totaal AV-blok',
    trage_r_top_progressie_over_voorwand: 'Trage R-top progressie over voorwand',
    tweede_graads_av_blok_type_mobitz: 'Tweede graads AV-blok type Mobitz',
    tweede_graads_av_blok_typewenckebach: 'Tweede graads AV-blok type Wenckebach',
    ventriculair_escaperitme: 'Ventriculair escaperitme',
    ventriculair_pacemakerritme: 'Ventriculair pacemakerritme',
    ventriculaire_tachycardie_non_sustained: 'Ventriculaire tachycardie non-sustained',
    ventriculaire_tachycardie_sustained: 'Ventriculaire tachycardie sustained',
    verticale_hartas: 'Verticale hartas',
    voorwand_infarct: 'Voorwandinfarct (doorgemaakt)',
    wel_ischemie: 'ST-segment (wel ischemie)',
} as const;

export type HolterDeviation = (typeof HolterDeviation)[keyof typeof HolterDeviation];

export type HolterDeviationKey = keyof typeof HolterDeviation;

export type HolterDeviations = {
    [K in Exclude<HolterDeviationKey, 'anders'>]: boolean | null;
} & {
    anders: string | null;
};

export const holterDeviationOptions: Array<CheckboxOption> = [
    {
        children: [
            { key: 'sinusritme', label: HolterDeviation.sinusritme },
            { key: 'atriumfibrilleren', label: HolterDeviation.atriumfibrilleren },
            { key: 'atriumflutter', label: HolterDeviation.atriumflutter },
            { key: 'sinusaritmie', label: HolterDeviation.sinusaritmie },
            { key: 'sa_block', label: HolterDeviation.sa_block },
            { key: 'sinusarrest', label: HolterDeviation.sinusarrest },
            { key: 'ectopisch_boezemritme', label: HolterDeviation.ectopisch_boezemritme },
            { key: 'atriale_tachycardie', label: HolterDeviation.atriale_tachycardie },
            { key: 'sinustachycardie', label: HolterDeviation.sinustachycardie },
            {
                key: 'ventriculaire_tachycardie_sustained',
                label: HolterDeviation.ventriculaire_tachycardie_sustained,
            },
            {
                key: 'ventriculaire_tachycardie_non_sustained',
                label: HolterDeviation.ventriculaire_tachycardie_non_sustained,
            },
            { key: 'torsades_des_pointes', label: HolterDeviation.torsades_des_pointes },
            {
                key: 'supraventriculair_ritmestoornis',
                label: HolterDeviation.supraventriculair_ritmestoornis,
            },
            { key: 'av_nodaal_ritme', label: HolterDeviation.av_nodaal_ritme },
            { key: 'avnrt', label: HolterDeviation.avnrt },
            {
                key: 'geblokkeerde_pac_bij_av_geleiding',
                label: HolterDeviation.geblokkeerde_pac_bij_av_geleiding,
            },
            { key: 'prematuur_atriaal_complex', label: HolterDeviation.prematuur_atriaal_complex },
            {
                key: 'prematuur_ventriculair_complex',
                label: HolterDeviation.prematuur_ventriculair_complex,
            },
            { key: 'fusie_complex', label: HolterDeviation.fusie_complex },
            { key: 'ventriculair_pacemakerritme', label: HolterDeviation.ventriculair_pacemakerritme },
            { key: 'ddd_pacemakerritme', label: HolterDeviation.ddd_pacemakerritme },
            { key: 'atriaal_pacemakerritme', label: HolterDeviation.atriaal_pacemakerritme },
            { key: 'ventriculair_escaperitme', label: HolterDeviation.ventriculair_escaperitme },
            { key: 'niet_te_bepalen_ritme', label: HolterDeviation.niet_te_bepalen_ritme },
        ],
        key: 'ritme',
        label: HolterDeviation.ritme,
    },
    {
        children: [
            { key: 'normale_geleiding', label: HolterDeviation.normale_geleiding },
            { key: 'eerste_graads_av_blok', label: HolterDeviation.eerste_graads_av_blok },
            {
                key: 'tweede_graads_av_blok_type_mobitz',
                label: HolterDeviation.tweede_graads_av_blok_type_mobitz,
            },
            {
                key: 'tweede_graads_av_blok_typewenckebach',
                label: HolterDeviation.tweede_graads_av_blok_typewenckebach,
            },
            { key: 'totaal_av_blok', label: HolterDeviation.totaal_av_blok },
        ],
        key: 'av_geleiding',
        label: HolterDeviation.av_geleiding,
    },
    {
        children: [
            { key: 'qrs_normale_breedte', label: HolterDeviation.qrs_normale_breedte },
            { key: 'fasciculus_anticus_blok', label: HolterDeviation.fasciculus_anticus_blok },
            { key: 'fasciculus_posticus_blok', label: HolterDeviation.fasciculus_posticus_blok },
            { key: 'compleet_rbtb', label: HolterDeviation.compleet_rbtb },
            { key: 'incompleet_rbtb', label: HolterDeviation.incompleet_rbtb },
            { key: 'compleet_lbtb', label: HolterDeviation.compleet_lbtb },
            { key: 'incompleet_lbtb', label: HolterDeviation.incompleet_lbtb },
            {
                key: 'aspecifieke_iv_geleidingsvertraging',
                label: HolterDeviation.aspecifieke_iv_geleidingsvertraging,
            },
        ],
        key: 'qrs_geleiding',
        label: HolterDeviation.qrs_geleiding,
    },
    {
        children: [
            { key: 'normaal_voltage', label: HolterDeviation.normaal_voltage },
            {
                key: 'laag_voltage_in_standaard_afleidingen',
                label: HolterDeviation.laag_voltage_in_standaard_afleidingen,
            },
            { key: 'tekenen_van_lvh', label: HolterDeviation.tekenen_van_lvh },
            { key: 'tekenen_van_rvh', label: HolterDeviation.tekenen_van_rvh },
            { key: 'pre_exitatie', label: HolterDeviation.pre_exitatie },
            {
                key: 'trage_r_top_progressie_over_voorwand',
                label: HolterDeviation.trage_r_top_progressie_over_voorwand,
            },
            { key: 'voorwand_infarct', label: HolterDeviation.voorwand_infarct },
            { key: 'onderwand_infarct', label: HolterDeviation.onderwand_infarct },
            { key: 'hoog_lateraal_infarct', label: HolterDeviation.hoog_lateraal_infarct },
            { key: 'inferolateraal_infarct', label: HolterDeviation.inferolateraal_infarct },
            { key: 'achterwand_infarct', label: HolterDeviation.achterwand_infarct },
        ],

        key: 'qrs_vorm',
        label: HolterDeviation.qrs_vorm,
    },
    {
        children: [
            { key: 'geen_ischemie', label: HolterDeviation.geen_ischemie },
            {
                key: 'secundaire_repolarisatie_stoornissen',
                label: HolterDeviation.secundaire_repolarisatie_stoornissen,
            },
            {
                key: 'aspecifieke_repolarisatiestoornissen',
                label: HolterDeviation.aspecifieke_repolarisatiestoornissen,
            },
        ],
        key: 'st_segment',
        label: HolterDeviation.st_segment,
    },
    {
        children: [
            { key: 'intermediair', label: HolterDeviation.intermediair },
            { key: 'linker_asdeviatie', label: HolterDeviation.linker_asdeviatie },
            { key: 'rechter_asdeviatie', label: HolterDeviation.rechter_asdeviatie },
            { key: 'extreme_as', label: HolterDeviation.extreme_as },
            { key: 'horizontale_hartas', label: HolterDeviation.horizontale_hartas },
            { key: 'verticale_hartas', label: HolterDeviation.verticale_hartas },
        ],
        key: 'elektrische_hartas',
        label: HolterDeviation.elektrische_hartas,
    },
    {
        children: [
            { key: 'linker_boezem_overbelasting', label: HolterDeviation.linker_boezem_overbelasting },
            { key: 'rechter_boezem_overbelasting', label: HolterDeviation.rechter_boezem_overbelasting },
            { key: 'prominente_u_golf', label: HolterDeviation.prominente_u_golf },
        ],
        key: 'overig',
        label: HolterDeviation.overig,
    },
    {
        isTextInput: true,
        key: 'anders',
        label: HolterDeviation.anders,
    },
];

const deviationBooleanKeys = Object.keys(HolterDeviation).filter((key) => key !== 'anders') as Array<
    keyof typeof HolterDeviation
>;

const deviations = deviationBooleanKeys.reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {} as Record<HolterDeviationKey, boolean>
);

export const deviationsInitialData: HolterDeviations = {
    ...deviations,
    anders: null,
};
